import { Link } from 'react-router-dom';
import { ArrowRight, Crown, Flame, Heart, CheckCircle } from 'lucide-react';

const SIFAH_IMG = "https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/wmz0wks1_Sifah%20Basmati.jpeg";
const NOURA_IMG = "https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/bzfprd3v_Noura%20Basmati.png";
const APNA_DANA_IMG = "https://customer-assets.emergentagent.com/job_global-rice-craft/artifacts/15sb2ndu_Apna%20Dana.PNG";

const brands = [
  {
    id: 'sifah',
    name: 'SIFAH',
    tagline: 'Our Flagship Premium Basmati Experience',
    description:
      "Sifah is the flagship brand of Sawariya Global Exports, created exclusively for international markets seeking the finest Indian Basmati Rice. Featuring premium 1121 Super Premium Basmati Rice, Sifah offers exceptional grain length, rich aroma, superior cooking performance, and world-class quality standards.",
    image: SIFAH_IMG,
    icon: Crown,
    accent: 'from-[#0F2D1F] via-[#1A4D2E] to-[#0A1F15]',
    badgeBg: 'bg-[#0F2D1F]',
    badgeText: 'text-gold',
    ringClass: 'ring-gold/40',
    highlights: [
      'Premium 1121 Super Premium Basmati Rice',
      'Extra Long Grain',
      'Naturally Aromatic',
      'Export Quality Assured',
      'Crafted for Global Consumers'
    ]
  },
  {
    id: 'noura',
    name: 'NOURA',
    tagline: 'The Perfect Rice for Every Biryani',
    description:
      "Noura is our specially developed Biryani Rice brand, offering carefully selected premium Sella Rice varieties. Designed for consumers who seek authentic flavor, excellent grain separation, and outstanding cooking performance, Noura brings restaurant-quality biryani experiences to every home.",
    image: NOURA_IMG,
    icon: Flame,
    accent: 'from-[#5A0F1A] via-[#7A1A26] to-[#3D0810]',
    badgeBg: 'bg-[#5A0F1A]',
    badgeText: 'text-[#F4D58D]',
    ringClass: 'ring-[#F4D58D]/40',
    highlights: [
      'Premium Sella Biryani Rice',
      'Excellent Grain Separation',
      'Rich Taste & Texture',
      'Ideal for Biryani & Traditional Rice Dishes',
      'Trusted Quality'
    ]
  },
  {
    id: 'apna-dana',
    name: 'APNA DANA',
    tagline: 'Royal Taste for Every Family',
    description:
      "Apna Dana was created with a simple belief — no family should be left behind from experiencing the delicious taste of Indian rice. Offering dependable quality at an affordable price, Apna Dana delivers great value for daily meals while maintaining the taste and satisfaction consumers love.",
    image: APNA_DANA_IMG,
    icon: Heart,
    accent: 'from-[#B8451C] via-[#D85C2A] to-[#8A2F13]',
    badgeBg: 'bg-[#B8451C]',
    badgeText: 'text-[#FFE9B0]',
    ringClass: 'ring-[#FFE9B0]/40',
    highlights: [
      'Economical Daily Use Rice',
      'Great Value for Money',
      'Consistent Quality',
      'Family-Friendly Choice',
      'Affordable Without Compromise'
    ]
  }
];

const Brands = () => {
  return (
    <main data-testid="brands-page">
      {/* Hero */}
      <section
        data-testid="brands-hero"
        className="relative min-h-[55vh] flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1586201375761-83865001e31c?w=1920')`
          }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="crest-watermark crest-watermark-center" style={{ backgroundImage: "url('/sawariya-logo.png')" }} aria-hidden="true" />

        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 pt-24 pb-12 text-center">
          <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block animate-fade-up">
            House of Brands
          </span>
          <h1
            data-testid="brands-title"
            className="font-heading text-4xl md:text-6xl font-bold text-white mb-4 animate-fade-up delay-100"
          >
            Our <span className="text-gold">Brands</span>
          </h1>
          <p className="font-body text-lg text-white/80 max-w-3xl mx-auto animate-fade-up delay-200">
            From super-premium Basmati to everyday family favorites — our brands are crafted to serve diverse consumer needs across international markets while delivering the authentic taste of Indian rice.
          </p>
        </div>
      </section>

      {/* Brand Cards */}
      <section data-testid="brands-list" className="py-20 md:py-28 wheat-pattern circle-watermark relative">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 space-y-20">
          {brands.map((brand, idx) => (
            <div
              key={brand.id}
              data-testid={`brand-detail-${brand.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center ${
                idx % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''
              }`}
            >
              {/* Pack Image */}
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${brand.accent} rounded-3xl blur-3xl opacity-30 scale-95`} />
                <div className={`relative bg-gradient-to-br ${brand.accent} rounded-3xl p-8 md:p-12 ring-2 ${brand.ringClass} shadow-2xl`}>
                  <img
                    src={brand.image}
                    alt={`${brand.name} packaging`}
                    className="w-full h-auto max-h-[560px] object-contain mx-auto drop-shadow-2xl"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Brand Info */}
              <div>
                <div className={`inline-flex items-center gap-3 ${brand.badgeBg} ${brand.badgeText} px-4 py-2 rounded-full text-xs tracking-[0.25em] uppercase font-semibold mb-5`}>
                  <brand.icon size={14} />
                  Brand 0{idx + 1}
                </div>
                <h2 className="font-heading text-5xl md:text-6xl font-bold text-navy mb-3 tracking-tight">
                  {brand.name}
                </h2>
                <p className="font-accent text-xl md:text-2xl text-gold italic mb-6">
                  &ldquo;{brand.tagline}&rdquo;
                </p>
                <div className="gold-line bg-gold mb-6" />
                <p className="font-body text-base md:text-lg text-gray-700 leading-relaxed mb-8">
                  {brand.description}
                </p>
                <div className="space-y-3">
                  {brand.highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="font-body text-gray-700">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="brands-cta" className="py-20 md:py-28 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
        }} />
        <div className="container mx-auto px-4 md:px-8 max-w-4xl relative z-10 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
            Build Your Market With Our <span className="text-gold">Brands</span>
          </h2>
          <p className="font-body text-lg text-white/80 mb-8">
            Whether you&rsquo;re seeking world-class premium Basmati, specialized biryani rice, or affordable daily-use rice &mdash; we have a brand tailored to every consumer.
          </p>
          <Link
            to="/contact"
            data-testid="brands-contact-btn"
            className="inline-flex items-center gap-3 bg-gold text-navy-dark px-8 py-4 rounded-full font-body text-sm tracking-[0.18em] uppercase font-semibold hover:bg-gold-light hover:-translate-y-0.5 transition-all"
          >
            Partner With Us
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Brands;
